import type { ReactNode } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReloadOutlined } from '@ant-design/icons';
import { UserCog } from 'lucide-react';

import { PageHeader, TabsTruot } from '../../../components/shared';
import { Button } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KhongCoQuyen } from '../components/chung';
import { TabRole } from '../components/danh-ba/TabRole';
import { TabUser } from '../components/danh-ba/TabUser';
import { QUYEN } from '../constants';
import { KHOA_RPA } from '../hooks/useRpa';
import { THAM_SO_TAB } from '../nghiep-vu/tab';

const TAB = ['user', 'role'] as const;
type TabDanhBa = (typeof TAB)[number];

/**
 * Quản lý User OpenCore (`/rpa/opencore-users?tab=user|role`, `RpaOpenCoreUsersView` của native): User và role trên
 * OpenCore của tenant. Mỗi tab theo quyền đọc riêng (`rpa:opencore-user:read`, `rpa:opencore-role:read`), thiếu thì ẩn
 * tab. Tab đang mở ghi vào `?tab=` (thay chỗ, không thêm lịch sử), cùng cách với màn Cấu hình lỗi & kết nối.
 */
export function UserOpenCoreView() {
  const { t } = useT();
  const qc = useQueryClient();
  const quyen = useModulePermissions(QUYEN);
  const [thamSo, setThamSo] = useSearchParams();

  const duocXem = TAB.filter((k) => (k === 'user' ? quyen.opencoreUserDoc : quyen.opencoreRoleDoc));
  const moSan = duocXem.find((k) => k === thamSo.get(THAM_SO_TAB)) ?? duocXem[0] ?? null;
  const [daGhi, setDaGhi] = useState(moSan);
  const [lanDung, setLanDung] = useState(0);
  if (moSan !== daGhi) {
    setDaGhi(moSan);
    setLanDung((n) => n + 1);
  }

  const noiDung: Record<TabDanhBa, () => ReactNode> = {
    user: () => (
      <TabUser suaDuoc={quyen.opencoreUserSua} ganRoleDuoc={quyen.opencoreRoleSua} docRoleDuoc={quyen.opencoreRoleDoc} />
    ),
    role: () => <TabRole suaDuoc={quyen.opencoreRoleSua} docUserDuoc={quyen.opencoreUserDoc || quyen.licenseDoc} />,
  };
  const nhan: Record<TabDanhBa, string> = { user: t('rpaDanhBa.tabUser'), role: t('rpaDanhBa.tabRole') };
  const tieuDe = t('nav.muc.quanLyUserOpenCore');

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: tieuDe }]}
        titleIcon={<UserCog size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaDanhBa.moTa')}
        actions={
          moSan ? (
            <Button variant="outline" size="md" icon={<ReloadOutlined />} onClick={() => void qc.invalidateQueries({ queryKey: KHOA_RPA.danhBaOpenCore })}>
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />
      {moSan === null ? (
        <KhongCoQuyen quyen={`${QUYEN.opencoreUserDoc}, ${QUYEN.opencoreRoleDoc}`} />
      ) : (
        <TabsTruot
          key={lanDung}
          xuongDong
          defaultActiveKey={moSan}
          onChange={(key) => {
            setDaGhi(key as TabDanhBa);
            setThamSo(
              (cu) => {
                const moi = new URLSearchParams(cu);
                moi.set(THAM_SO_TAB, key);
                return moi;
              },
              { replace: true },
            );
          }}
          items={duocXem.map((k) => ({ key: k, label: nhan[k], children: noiDung[k]() }))}
        />
      )}
    </div>
  );
}
